import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAppearance } from "../../contexts/AppearanceContext";
import {
  Activity, LogIn, Users, Zap, TrendingUp, CheckSquare, UserPlus, Building2,
  MessageCircle, FileSignature, Mail, Package, Loader2, Download, ChevronLeft,
  ChevronRight, Clock, BarChart2, ArrowUpDown, Eye, StickyNote, Brain, Receipt,
  Trophy, Medal,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_LABELS = {
  auth: "Login", negocios: "Negócios", contatos: "Contatos", empresas: "Empresas",
  produtos: "Produtos", pipelines: "Pipelines", pipeline_stages: "Etapas", profiles: "Perfis",
  usuarios: "Usuários", campos_personalizados: "Campos", aparencia: "Aparência",
  form_layout: "Formulários", module_settings: "Módulos", tenants: "Tenants",
  tarefas: "Tarefas", email: "E-mail", automacoes: "Automações",
  kanban_card_layouts: "Kanban", stage_fields: "Campos de Etapa",
  motivos_perda: "Motivos de Perda", leads: "Leads",
};

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16"];
const PIE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16", "#0EA5E9", "#D946EF"];

function KpiCard({ icon: Icon, title, value, sub, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "18" }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{title}</p>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value ?? "—"}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function RankBadge({ position }) {
  if (position === 1) return <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><Trophy className="w-3.5 h-3.5 text-amber-500" /></div>;
  if (position === 2) return <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0"><Medal className="w-3.5 h-3.5 text-slate-400" /></div>;
  if (position === 3) return <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0"><Medal className="w-3.5 h-3.5 text-orange-400" /></div>;
  return <div className="w-6 h-6 rounded-full bg-slate-50 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-slate-400">{position}</div>;
}

export default function AtividadesReport({ filterHook }) {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedPage, setFeedPage] = useState(1);
  const [feedModule, setFeedModule] = useState("");
  const [sortField, setSortField] = useState("total_actions");
  const [sortDir, setSortDir] = useState("desc");
  const [horariosView, setHorariosView] = useState("heatmap");
  const [horariosUser, setHorariosUser] = useState("all");

  const buildFilters = useCallback(() => {
    return filterHook.buildApiFilters({
      period: { start: filterHook.filters.periodStart + "T00:00:00Z", end: filterHook.filters.periodEnd + "T23:59:59Z", field: "created_at" },
    });
  }, [filterHook]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reports/activities`, { filters: buildFilters() }, { withCredentials: true });
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [buildFilters]);

  const fetchFeed = useCallback(async (page = 1, mod = feedModule) => {
    setFeedLoading(true);
    try {
      const res = await axios.post(`${API}/reports/activity-feed`, { filters: buildFilters(), page, limit: 30, module_filter: mod }, { withCredentials: true });
      setFeed(res.data);
      setFeedPage(page);
    } catch { setFeed(null); }
    setFeedLoading(false);
  }, [buildFilters, feedModule]);

  useEffect(() => {
    fetchData();
    fetchFeed(1, feedModule);
  }, [filterHook.filters.periodStart, filterHook.filters.periodEnd, filterHook.filters.responsavelIds]); // eslint-disable-line

  const sortUsers = (users) => [...users].sort((a, b) => sortDir === "desc" ? (b[sortField] || 0) - (a[sortField] || 0) : (a[sortField] || 0) - (b[sortField] || 0));

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const exportExcel = () => {
    if (!data?.users) return;
    const ws = XLSX.utils.json_to_sheet(data.users.map(u => ({
      "#": data.users.indexOf(u) + 1, "Usuário": u.name, "E-mail": u.email,
      "Logins": u.logins, "Último Login": u.last_login ? new Date(u.last_login).toLocaleString("pt-BR") : "—",
      "Deals Visualizados": u.negocios_visualizados, "Deals Criados": u.negocios_criados,
      "Deals Editados": u.negocios_editados, "Deals Movidos": u.negocios_movidos,
      "Deals Ganhos": u.negocios_ganhos, "Deals Perdidos": u.negocios_perdidos,
      "Tarefas Criadas": u.tarefas_criadas, "Tarefas Concluídas": u.tarefas_concluidas,
      "Contatos Criados": u.contatos_criados, "Empresas Criadas": u.empresas_criadas,
      "WhatsApp Enviados": u.whatsapp_enviados, "Anotações": u.anotacoes_criadas,
      "Contratos": u.contratos_emitidos, "Cobranças": u.cobrancas_emitidas,
      "Resumos IA": u.resumos_ia, "Deals Abertos": u.deals_abertos,
      "Ativ./Deal": u.atividades_por_deal, "Total Atividades": u.total_actions,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Atividades");
    XLSX.writeFile(wb, `atividades_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading && !data) return <div className="flex items-center justify-center h-48 text-slate-400 text-sm"><Loader2 className="w-5 h-5 animate-spin mr-2" />Carregando...</div>;
  if (!data) return <p className="text-sm text-slate-400">Sem dados disponíveis.</p>;

  const { totals, users, by_day, by_module, by_action } = data;
  const sortedUsers = sortUsers(users);

  const SortHeader = ({ field, children, className = "" }) => (
    <th className={`px-2 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none whitespace-nowrap ${className}`} onClick={() => toggleSort(field)}>
      <span className="inline-flex items-center gap-0.5">{children}<ArrowUpDown className={`w-2.5 h-2.5 ${sortField === field ? "text-slate-700" : "text-slate-300"}`} /></span>
    </th>
  );

  // Pie chart data for action types
  const pieData = (by_action || []).slice(0, 10).map((a, i) => ({ name: a.action, value: a.count, fill: PIE_COLORS[i % PIE_COLORS.length] }));

  // User ranking bar data (top 8)
  const rankingData = users.slice(0, 8).map((u, i) => ({ name: u.name.split(" ")[0], total: u.total_actions, color: COLORS[i % COLORS.length] }));

  return (
    <div className="space-y-6">
      {/* KPIs - 2 rows */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Zap} title="Total de Atividades" value={totals.total_actions?.toLocaleString("pt-BR")} sub="no período" color={tc500} />
        <KpiCard icon={LogIn} title="Total de Logins" value={totals.total_logins?.toLocaleString("pt-BR")} sub="acessos ao sistema" color="#8B5CF6" />
        <KpiCard icon={Users} title="Usuários Ativos" value={`${totals.active_users}/${totals.total_users}`} sub="com pelo menos 1 ação" color="#10B981" />
        <KpiCard icon={Activity} title="Média por Usuário" value={totals.avg_actions_per_user} sub="atividades/usuário" color="#F59E0B" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={TrendingUp} title="Deals Criados" value={totals.total_negocios_criados} sub="novos negócios" color="#3B82F6" />
        <KpiCard icon={CheckSquare} title="Tarefas Concluídas" value={totals.total_tarefas_concluidas} sub="no período" color="#10B981" />
        <KpiCard icon={MessageCircle} title="WhatsApp Enviados" value={totals.total_whatsapp} sub="mensagens" color="#22C55E" />
        <KpiCard icon={Eye} title="Deals Visualizados" value={totals.total_deals_visualizados} sub="aberturas de negócio" color="#6366F1" />
      </div>

      {/* Charts Row 1: Activity by Day + Action Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" style={{ color: tc500 }} />Atividades por Dia</h3>
          {by_day.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={v => { const d = new Date(v + "T12:00:00"); return `${d.getDate()}/${d.getMonth() + 1}`; }} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <RT contentStyle={{ fontSize: 12 }} labelFormatter={v => new Date(v + "T12:00:00").toLocaleDateString("pt-BR")} />
                <Line type="monotone" dataKey="count" stroke={tc500} strokeWidth={2} dot={{ r: 2 }} name="Atividades" />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">Sem dados no período</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><BarChart2 className="w-4 h-4" style={{ color: tc500 }} />Distribuição de Ações</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={35} paddingAngle={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}
                  style={{ fontSize: 9 }}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <RT contentStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">Sem dados</p>}
        </div>
      </div>

      {/* Stacked Bar: Composição de Atividades por Usuário */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Trophy className="w-4 h-4" style={{ color: "#F59E0B" }} />Composição de Atividades por Usuário</h3>
        {users.length > 0 ? (() => {
          const STACK_KEYS = [
            { key: "negocios_criados", label: "Deals criados", color: "#3B82F6" },
            { key: "negocios_editados", label: "Deals editados", color: "#6366F1" },
            { key: "negocios_movidos", label: "Deals movidos", color: "#0EA5E9" },
            { key: "negocios_ganhos", label: "Deals ganhos", color: "#10B981" },
            { key: "negocios_perdidos", label: "Deals perdidos", color: "#EF4444" },
            { key: "tarefas_concluidas", label: "Tarefas", color: "#F59E0B" },
            { key: "whatsapp_enviados", label: "WhatsApp", color: "#22C55E" },
            { key: "anotacoes_criadas", label: "Anotações", color: "#8B5CF6" },
            { key: "contratos_emitidos", label: "Contratos", color: "#EC4899" },
            { key: "cobrancas_emitidas", label: "Cobranças", color: "#F97316" },
            { key: "contatos_criados", label: "Contatos", color: "#14B8A6" },
            { key: "resumos_ia", label: "Resumos IA", color: "#A855F7" },
          ];
          const stackData = users.map(u => {
            const row = { name: u.name.split(" ")[0] };
            STACK_KEYS.forEach(sk => { row[sk.key] = u[sk.key] || 0; });
            return row;
          });
          return (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stackData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <RT contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {STACK_KEYS.map(sk => (
                  <Bar key={sk.key} dataKey={sk.key} name={sk.label} stackId="a" fill={sk.color} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          );
        })() : <p className="text-xs text-slate-400 text-center py-8">Sem dados</p>}
      </div>

      {/* Chart: By Module */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><BarChart2 className="w-4 h-4" style={{ color: tc500 }} />Atividades por Módulo</h3>
          {by_module.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={by_module.slice(0, 8).map(m => ({ ...m, label: MODULE_LABELS[m.module] || m.module }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={90} />
                <RT contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" name="Ações" radius={[0, 4, 4, 0]}>
                  {by_module.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">Sem dados</p>}
      </div>

      {/* Horários de Atividade */}
      <HorariosSection
        data={data}
        view={horariosView}
        setView={setHorariosView}
        selectedUser={horariosUser}
        setSelectedUser={setHorariosUser}
        users={users}
        tc500={tc500}
      />

      {/* User Engagement Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Users className="w-4 h-4" style={{ color: tc500 }} />Engajamento por Usuário</h3>
          <Button variant="outline" size="sm" onClick={exportExcel} className="gap-2 text-slate-600 h-8 text-xs"><Download className="w-3.5 h-3.5" />Exportar</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-y border-slate-100 bg-slate-50/50">
              <tr>
                <th className="px-2 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider w-8">#</th>
                <th className="px-2 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50/50">Usuário</th>
                <SortHeader field="logins">Logins</SortHeader>
                <SortHeader field="negocios_visualizados">Acessos</SortHeader>
                <SortHeader field="negocios_criados">Criados</SortHeader>
                <SortHeader field="negocios_editados">Editados</SortHeader>
                <SortHeader field="negocios_movidos">Movidos</SortHeader>
                <SortHeader field="negocios_ganhos">Ganhos</SortHeader>
                <SortHeader field="negocios_perdidos">Perdidos</SortHeader>
                <SortHeader field="tarefas_concluidas">Tarefas</SortHeader>
                <SortHeader field="contatos_criados">Contatos</SortHeader>
                <SortHeader field="whatsapp_enviados">WhatsApp</SortHeader>
                <SortHeader field="anotacoes_criadas">Notas</SortHeader>
                <SortHeader field="contratos_emitidos">Contratos</SortHeader>
                <SortHeader field="cobrancas_emitidas">Cobranças</SortHeader>
                <SortHeader field="resumos_ia">IA</SortHeader>
                <SortHeader field="deals_abertos">Abertos</SortHeader>
                <SortHeader field="atividades_por_deal">Ativ/Deal</SortHeader>
                <SortHeader field="total_actions" className="text-right">Total</SortHeader>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u, i) => {
                const rank = users.findIndex(x => x.user_id === u.user_id) + 1;
                const maxTotal = Math.max(...users.map(x => x.total_actions), 1);
                const pct = Math.round((u.total_actions / maxTotal) * 100);
                return (
                  <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    <td className="px-2 py-2"><RankBadge position={rank} /></td>
                    <td className="px-2 py-2 sticky left-0 bg-white">
                      <div className="flex items-center gap-2 min-w-[130px]">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: COLORS[(rank - 1) % COLORS.length] }}>
                          {u.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-800 truncate max-w-[110px]">{u.name}</p>
                          <p className="text-[10px] text-slate-400">{u.last_login ? new Date(u.last_login).toLocaleDateString("pt-BR") : "Nunca"}</p>
                        </div>
                      </div>
                    </td>
                    <Cell2>{u.logins}</Cell2>
                    <Cell2 muted>{u.negocios_visualizados}</Cell2>
                    <Cell2>{u.negocios_criados}</Cell2>
                    <Cell2>{u.negocios_editados}</Cell2>
                    <Cell2>{u.negocios_movidos}</Cell2>
                    <Cell2 color="emerald">{u.negocios_ganhos}</Cell2>
                    <Cell2 color="rose">{u.negocios_perdidos}</Cell2>
                    <Cell2>{u.tarefas_concluidas}<span className="text-slate-300">/{u.tarefas_criadas}</span></Cell2>
                    <Cell2>{u.contatos_criados}</Cell2>
                    <Cell2>{u.whatsapp_enviados}</Cell2>
                    <Cell2>{u.anotacoes_criadas}</Cell2>
                    <Cell2>{u.contratos_emitidos}</Cell2>
                    <Cell2>{u.cobrancas_emitidas}</Cell2>
                    <Cell2 muted>{u.resumos_ia}</Cell2>
                    <Cell2>{u.deals_abertos}</Cell2>
                    <td className="px-2 py-2 text-xs text-center">
                      <span className={`font-medium ${u.atividades_por_deal >= 10 ? "text-emerald-600" : u.atividades_por_deal >= 3 ? "text-amber-600" : "text-rose-500"}`}>{u.atividades_por_deal}</span>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: tc500 }} /></div>
                        <span className="text-xs font-bold text-slate-700 w-8 text-right">{u.total_actions}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {sortedUsers.length === 0 && (
                <tr><td colSpan={19} className="text-center py-8 text-sm text-slate-400">Nenhuma atividade no período</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Activity Feed */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Clock className="w-4 h-4" style={{ color: tc500 }} />Feed de Atividades</h3>
          <Select value={feedModule} onValueChange={v => { const mod = v === "all" ? "" : v; setFeedModule(mod); fetchFeed(1, mod); }}>
            <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="Todos os módulos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os módulos</SelectItem>
              {Object.entries(MODULE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="px-4 pb-4">
          {feedLoading && !feed ? (
            <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-4 h-4 animate-spin mr-2" />Carregando...</div>
          ) : feed?.items?.length > 0 ? (
            <>
              <div className="space-y-1 mt-2">
                {feed.items.map((item, i) => {
                  const icon = getActionIcon(item.module, item.event_type);
                  return (
                    <div key={item.id || i} className="flex items-start gap-3 py-2 px-2 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: tc500 + "15" }}>
                        {icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-700">
                          <span className="font-semibold">{item.user_name || "Sistema"}</span>
                          <span className="text-slate-400"> · </span>
                          <span>{item.description || `${item.action} em ${MODULE_LABELS[item.module] || item.module}`}</span>
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-slate-200 text-slate-400">{MODULE_LABELS[item.module] || item.module}</Badge>
                          <span className="text-[10px] text-slate-300">{item.created_at ? new Date(item.created_at).toLocaleString("pt-BR") : ""}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {feed.pages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-3 border-t border-slate-100 mt-3">
                  <Button variant="outline" size="sm" disabled={feedPage <= 1} onClick={() => fetchFeed(feedPage - 1, feedModule)} className="h-7 text-xs gap-1"><ChevronLeft className="w-3 h-3" />Anterior</Button>
                  <span className="text-xs text-slate-400">{feedPage} de {feed.pages} ({feed.total} registros)</span>
                  <Button variant="outline" size="sm" disabled={feedPage >= feed.pages} onClick={() => fetchFeed(feedPage + 1, feedModule)} className="h-7 text-xs gap-1">Próximo<ChevronRight className="w-3 h-3" /></Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8">Nenhuma atividade registrada no período.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Cell2({ children, color, muted }) {
  const colorClass = color === "emerald" ? "text-emerald-600 font-medium" : color === "rose" ? "text-rose-500" : muted ? "text-slate-400" : "text-slate-600";
  return <td className={`px-2 py-2 text-xs text-center ${colorClass}`}>{children}</td>;
}

const WEEKDAY_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function HorariosSection({ data, view, setView, selectedUser, setSelectedUser, users, tc500 }) {
  if (!data) return null;

  const heatmapData = selectedUser === "all" ? data.heatmap : (data.heatmap_users?.[selectedUser] || []);
  const byHourData = selectedUser === "all" ? data.by_hour : (data.by_hour_users?.[selectedUser] || []);

  const maxCount = Math.max(...(heatmapData || []).map(d => d.count), 1);

  const getColor = (count) => {
    if (count === 0) return "#f8fafc";
    const intensity = Math.min(count / maxCount, 1);
    if (intensity < 0.25) return "#dbeafe";
    if (intensity < 0.5) return "#93c5fd";
    if (intensity < 0.75) return "#3b82f6";
    return "#1d4ed8";
  };

  // Extract unique dates from heatmap data, sorted
  const uniqueDates = [...new Set((heatmapData || []).map(d => d.date))].filter(Boolean).sort();

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Clock className="w-4 h-4" style={{ color: tc500 }} />Horários de Atividade
        </h3>
        <div className="flex items-center gap-2">
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os usuários</SelectItem>
              {users.map(u => <SelectItem key={u.user_id} value={u.name}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setView("heatmap")}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === "heatmap" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Mapa de Calor
            </button>
            <button onClick={() => setView("hours")}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === "hours" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Por Hora
            </button>
          </div>
        </div>
      </div>

      {view === "heatmap" ? (
        <div>
          <div className="overflow-x-auto">
            <div className="min-w-[650px]">
              {/* Header row with hours */}
              <div className="flex items-center gap-0.5 mb-1">
                <div className="w-[70px] flex-shrink-0" />
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="flex-1 text-center text-[9px] text-slate-400 font-medium">{String(h).padStart(2, "0")}</div>
                ))}
              </div>
              {/* Date rows */}
              <div className="max-h-[400px] overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
                {uniqueDates.map((dateStr) => {
                  const cell0 = (heatmapData || []).find(d => d.date === dateStr && d.hour === 0);
                  const dayLabel = cell0?.day_label || dateStr.slice(5);
                  const wdName = cell0?.weekday_name || "";
                  const isWeekend = wdName === "Sáb" || wdName === "Dom";
                  return (
                    <div key={dateStr} className={`flex items-center gap-0.5 mb-0.5 ${isWeekend ? "opacity-60" : ""}`}>
                      <div className="w-[70px] flex-shrink-0 flex items-center gap-1 pr-1 justify-end">
                        <span className="text-[10px] font-semibold text-slate-700">{dayLabel}</span>
                        <span className="text-[9px] text-slate-400">{wdName}</span>
                      </div>
                      {Array.from({ length: 24 }, (_, h) => {
                        const cell = (heatmapData || []).find(d => d.date === dateStr && d.hour === h);
                        const count = cell?.count || 0;
                        return (
                          <div key={h} className="flex-1 rounded-sm cursor-default relative group"
                            style={{ backgroundColor: getColor(count), minHeight: "20px" }}
                            title={`${dayLabel} (${wdName}) ${String(h).padStart(2, "0")}:00 — ${count} atividade(s)`}>
                            {count > 0 && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className={`text-[8px] font-bold ${count / maxCount > 0.5 ? "text-white" : "text-blue-700"}`}>{count}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              {uniqueDates.length === 0 && <p className="text-xs text-slate-400 text-center py-8">Sem atividades no período selecionado</p>}
              {/* Legend */}
              <div className="flex items-center justify-end gap-1.5 mt-3">
                <span className="text-[9px] text-slate-400">Menos</span>
                {["#f8fafc", "#dbeafe", "#93c5fd", "#3b82f6", "#1d4ed8"].map((c, i) => (
                  <div key={i} className="w-3 h-3 rounded-sm" style={{ backgroundColor: c }} />
                ))}
                <span className="text-[9px] text-slate-400">Mais</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {(byHourData || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={byHourData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="hour" tickFormatter={h => `${String(h).padStart(2, "0")}h`} tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <RT contentStyle={{ fontSize: 12 }} labelFormatter={h => `${String(h).padStart(2, "0")}:00`} />
                <Bar dataKey="count" name="Atividades" fill={tc500} radius={[4, 4, 0, 0]}>
                  {(byHourData || []).map((entry, i) => (
                    <Cell key={i} fill={entry.count > 0 ? tc500 : "#e2e8f0"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">Sem dados no período</p>}
        </div>
      )}

      {/* Transparency table: recent logs with local time */}
      {data.recent_logs?.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Últimas atividades registradas
              {data.timezone && <span className="text-slate-400 font-normal ml-1">({data.timezone})</span>}
            </p>
          </div>
          <div className="overflow-x-auto max-h-[250px] overflow-y-auto rounded-lg border border-slate-100">
            <table className="w-full text-left">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase">Data/Hora Local</th>
                  <th className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase">UTC</th>
                  <th className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase">Usuário</th>
                  <th className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase">Módulo</th>
                  <th className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase">Descrição</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_logs.map((log, i) => (
                  <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/50">
                    <td className="px-2 py-1.5 text-[11px] text-slate-700 font-medium whitespace-nowrap">{log.created_at_local}</td>
                    <td className="px-2 py-1.5 text-[10px] text-slate-400 whitespace-nowrap">{(log.created_at_utc || "").replace("+00:00", "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-2 py-1.5 text-[11px] text-slate-600">{log.user_name}</td>
                    <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-slate-200 text-slate-400">{MODULE_LABELS[log.module] || log.module}</Badge></td>
                    <td className="px-2 py-1.5 text-[11px] text-slate-500 max-w-[300px] truncate">{log.description || `${log.action} em ${log.module}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function getActionIcon(module, eventType) {
  const cls = "w-3.5 h-3.5";
  if (eventType === "whatsapp_enviado") return <MessageCircle className={cls} style={{ color: "#22C55E" }} />;
  if (eventType === "contrato_emitido") return <FileSignature className={cls} style={{ color: "#8B5CF6" }} />;
  if (eventType === "cobranca_emitida") return <Receipt className={cls} style={{ color: "#F59E0B" }} />;
  if (eventType === "anotacao_criada") return <StickyNote className={cls} style={{ color: "#6366F1" }} />;
  if (eventType === "ai_summary_gerado") return <Brain className={cls} style={{ color: "#EC4899" }} />;
  if (eventType === "negocio_visualizado") return <Eye className={cls} style={{ color: "#6366F1" }} />;
  switch (module) {
    case "auth": return <LogIn className={cls} style={{ color: "#8B5CF6" }} />;
    case "negocios": return <TrendingUp className={cls} style={{ color: "#3B82F6" }} />;
    case "contatos": return <UserPlus className={cls} style={{ color: "#10B981" }} />;
    case "empresas": return <Building2 className={cls} style={{ color: "#F59E0B" }} />;
    case "produtos": return <Package className={cls} style={{ color: "#EC4899" }} />;
    default: return <Activity className={cls} style={{ color: "#64748b" }} />;
  }
}
